import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { endpoints } from '../../environments/endpoints';
import { ApiErrorResponse, LaravelDefaultResponse } from '../Interfaces/response.interface';
import { Plan } from '../Interfaces/plan.interface';
import { CheckoutPreview } from '../Interfaces/checkout-preview.interface';
import { Onboarding } from '../Models/onboarding.model';
import { ApiService } from './api.service';
import { ApiRequestOptions } from '../Interfaces/api-request-options.interface';

@Injectable({
  providedIn: 'root',
})
export class PlanService {
  private readonly _api = inject(ApiService);

  getPlans(options: ApiRequestOptions = {}): Observable<Plan[]> {
    return this._api
      .get<LaravelDefaultResponse<Plan[]>>(endpoints.plans.list, options)
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  savePlan(planId: string, offerType: string): Observable<Onboarding> {
    return this._api
      .patch<LaravelDefaultResponse<Onboarding>>(endpoints.onboarding.savePlan, {
        plan_id: planId,
        offer_type: offerType,
      })
      .pipe(
        map((response) => new Onboarding(response.data)),
        catchError(this.handleError)
      );
  }

  saveLicences(userLicences: number, deviceLicences: number): Observable<Onboarding> {
    return this._api
      .patch<LaravelDefaultResponse<Onboarding>>(endpoints.onboarding.saveLicences, {
        user_licences: userLicences,
        device_licences: deviceLicences,
      })
      .pipe(
        map((response) => new Onboarding(response.data)),
        catchError(this.handleError)
      );
  }

  getCheckoutPreview(options: ApiRequestOptions = {}): Observable<CheckoutPreview> {
    return this._api
      .get<LaravelDefaultResponse<CheckoutPreview>>(endpoints.stripe.checkoutPreview, options)
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  initiateCheckout(): Observable<{ checkout_url: string }> {
    return this._api
      .post<LaravelDefaultResponse<{ checkout_url: string }>>(endpoints.stripe.initiateCheckout, {})
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  verifyCheckout(
    sessionId: string,
    options: ApiRequestOptions = {}
  ): Observable<LaravelDefaultResponse<unknown>> {
    return this._api
      .post<LaravelDefaultResponse<unknown>>(
        endpoints.stripe.verifyCheckout,
        { session_id: sessionId },
        options
      )
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unexpected error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else if (error.error && typeof error.error === 'object') {
      const apiError = error.error as ApiErrorResponse;
      errorMessage = apiError.message || `Server error: ${error.status}`;
    } else if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else {
      errorMessage = error.message || `Server error: ${error.status}`;
    }

    console.error('Plan API error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
